import { BaseWebComponentController, IListenerConfig } from '../../../framework/BaseWebComponentController';
import { CompaniesAppWebComponent } from '../component/CompaniesAppWebComponent';
import { CompaniesAppView } from '../view/CompaniesAppView';
import { templateCompaniesApp } from '../view/template';
import { CompaniesAppRepository } from '../repository/CompaniesAppRepository';
import { CompanyTypeData } from '../../../company/types/company-type-data';
import {
  CompaniesGridEvents,
  OnActivateCompany,
  OnCompanyNameClick,
  OnCompanyNameUpdates,
  OnDeleteCompany,
  OnSuspendCompany,
  OnModifyMspInProductTrialsRights,
} from '../../companiesGrid/events/events';
import { EditCompanyPanelWebComponent } from '../../../company/editCompany/editCompanyPanel';
import { GzDialogWebComponent } from '../../../ui/gzDialog';
import { CreateCompanyPanelWebComponent } from '../../../company/createCompany/createCompanyPanel';
import { CompanyType } from '../../../company/types/company-type';
import { Company } from '../../companiesGrid/types/companies-grid-company';
import { GzToastNotificationWebComponent } from '../../../ui/gzToastNotification';
import { ToastSeverity } from '../../../ui/gzToastNotification/type';
import { GzButtonWebComponent } from '../../../ui/gzButton';
import { sanitizeArray } from '../../../utils/sanitization.utils';
import { CompanyPanelEvent } from '../../../company/common/events/CompanyPanelEvent';
import { ActivateCompanyConfirmationWebComponent } from '../../dialogs/activateCompany';
import { SuspendCompanyConfirmationWebComponent } from '../../dialogs/suspendCompany';
import { translateFn } from "../../../utils/translator.helper";
import { showToast } from "../../../accounts/helpers/showToast";
import {
  MODIFY_MSP_IPT_FLAGS_ERROR_DISTRIBUTOR_DOES_NOT_RESELL_PRODUCT,
  MspInProductTrialId
} from "../../../company/common/mspInProductTrialsTab/types/msp-in-product-trials-types";
import { ConsoleRequest, DataProviderType } from '../../../utils/request';

export class CompaniesAppController extends BaseWebComponentController {
  public component: CompaniesAppWebComponent;
  public view: CompaniesAppView;
  public viewConstructor: typeof CompaniesAppView;
  public dataRepository: CompaniesAppRepository;
  public dataRepositoryConstructor: typeof CompaniesAppRepository;

  private editCompanyDialog: GzDialogWebComponent;
  private createCompanyDialog: GzDialogWebComponent;

  constructor(component: CompaniesAppWebComponent) {
    super(component);

    this.initialSetup({
      viewConstructor: CompaniesAppView,
      viewTemplate: templateCompaniesApp,
      dataRepositoryConstructor: CompaniesAppRepository,
    });
  }

  public computeEventListeners(): IListenerConfig[] {
    return [
      {
        element: this.view.dom.$companiesGrid,
        event: CompaniesGridEvents.OnCompanyNameClick,
        callback: (event: OnCompanyNameClick) => {
          this.showEditCompany(event.detail.companyId, event.detail.companyName);
        },
      },
      {
        element: this.view.dom.$companiesGrid,
        event: CompaniesGridEvents.OnAddCompany,
        callback: this.showCreateCompany,
      },
      {
        element: this.view.dom.$companiesGrid,
        event: CompaniesGridEvents.OnDeleteCompany,
        callback: this.showDeleteCompanyConfirmationWindow,
      },
      {
        element: this.view.dom.$companiesGrid,
        event: CompaniesGridEvents.OnActivateCompany,
        callback: this.activateCompanies,
      },
      {
        element: this.view.dom.$companiesGrid,
        event: CompaniesGridEvents.OnSuspendCompany,
        callback: this.suspendCompanies,
      },
      {
        element: this.view.dom.$companiesGrid,
        event: CompaniesGridEvents.OnModifyMspInProductTrialsRights,
        callback: this.modifyMspInProductTrialsRights,
      },
      {
        element: this.view.dom.$companiesGrid,
        event: CompaniesGridEvents.OnModifyMspInProductTrialsVisibility,
        callback: this.modifyMspInProductTrialsVisibility,
      },
    ];
  }

  async getDataForView(): Promise<{ companyTypes: CompanyTypeData[] }> {
    return {
      companyTypes: await this.dataRepository.getCompanyTypes(),
    };
  }

  public async showEditCompany(companyId: string, companyName: string) {
    const editCompanyPanel = new EditCompanyPanelWebComponent();
    editCompanyPanel.setAttribute('editedCompanyId', companyId);

    const onCloseHandler = this.maybeCloseEditCompanyPanel.bind(this, editCompanyPanel);
    const onHeadlineChangeHandler = this.updateDialogHeadline.bind(this, editCompanyPanel);
    const headline = this.dependencies.translateFn('Edit {companyName}', { companyName });

    const dialogConfig = {
      headline: headline,
      bodyContent: editCompanyPanel,
      isOpen: true,
      isWindow: true,
      closableOutsideBox: false,
      onCloseHandler: onCloseHandler,
    };

    this.editCompanyDialog = GzDialogWebComponent.buildDialogElement(
      dialogConfig,
      document.querySelector('.mainContainer') as HTMLElement
    );

    editCompanyPanel.addEventListener(CompanyPanelEvent.Close, onCloseHandler);
    editCompanyPanel.addEventListener(CompaniesGridEvents.OnCompanyNameUpdates, onHeadlineChangeHandler);
    editCompanyPanel.injectDependencies(this.dependencies);
    editCompanyPanel.sideMenuParentDialog = this.editCompanyDialog;

    await editCompanyPanel.start();
  }

  private async showCreateCompany() {
    const createCompanyPanel = new CreateCompanyPanelWebComponent();

    const dialogConfig = {
      headline: this.dependencies.translateFn('Add Company'),
      bodyContent: createCompanyPanel,
      isOpen: true,
      isWindow: true,
      closableOutsideBox: false,
      onCloseHandler: this.maybeCloseCreateCompanyPanel.bind(this, createCompanyPanel),
    };

    this.createCompanyDialog = GzDialogWebComponent.buildDialogElement(
      dialogConfig,
      document.querySelector('.mainContainer') as HTMLElement
    );

    createCompanyPanel.addEventListener(CompanyPanelEvent.Close, this.closeCreateCompanyWindow.bind(this));
    createCompanyPanel.injectDependencies(this.dependencies);

    await createCompanyPanel.start();
  }

  private closeCreateCompanyWindow() {
    if (this.createCompanyDialog === null) {
      return;
    }

    this.createCompanyDialog.dismiss();
    // reload grid
    this.view.dom.$companiesGrid.reload();
  }

  private maybeCloseEditCompanyPanel(editCompanyPanelWebComp: EditCompanyPanelWebComponent) {

    // check if a product trial video is started
    if (editCompanyPanelWebComp.isPlayingVideo) {
      return;
    }

    const close = () => {
      // close dialog
      this.editCompanyDialog.dismiss();
      // reload grid
      this.view.dom.$companiesGrid.reload();
    };

    if (editCompanyPanelWebComp.hasUnsavedChanges) {
      this.showCancelWarningDialog(() => {
        close();
      });
    } else {
      close();
    }
  }

  private maybeCloseCreateCompanyPanel(createCompanyPanelWebComp: CreateCompanyPanelWebComponent) {
    const close = () => {
      // close dialog
      this.createCompanyDialog.dismiss();
      // reload grid
      this.view.dom.$companiesGrid.reload();
    };

    if (createCompanyPanelWebComp.hasUnsavedChanges) {
      this.showCancelWarningDialog(() => {
        close();
      });
    } else {
      close();
    }
  }

  private showCancelWarningDialog(confirmCallback: () => void) {
    const windowBodyHtml = `
      <p>
        ${this.dependencies.translateFn('Changes you have made will be lost.')}
      </p>`;

    const windowFooterHtml = `<gz-button id='confirm' type='outline' color='negative' class='mr-2'>
       ${this.dependencies.translateFn('Discard')}
      </gz-button>
      <gz-button id='cancel' type='outline' color='secondary'>
        ${this.dependencies.translateFn('Keep changes')}
      </gz-button>`;

    const windowTitle = this.dependencies.translateFn('Discard changes?');

    GzDialogWebComponent.computeCustomDialog(windowBodyHtml, windowFooterHtml, windowTitle, null, confirmCallback);
  }

  private showDeleteCompanyConfirmationWindow(event: OnDeleteCompany) {
    const companyIds = event.detail.companyIds;

    const windowBodyHtml = `
      <p>
        ${this.dependencies.translateFn(
          'Once deleted, companies cannot be restored. You will lose all associated policies, reports, and settings. '
        )}
      </p>`;

    const windowFooterHtml = `<gz-button id='confirm' type='outline' color='negative' class='mr-2'>
       ${this.dependencies.translateFn('Delete')}
      </gz-button>
      <gz-button id='cancel' type='outline' color='secondary'>
        ${this.dependencies.translateFn('Cancel')}
      </gz-button>`;

    const windowTitle =
      companyIds.length === 1
        ? this.dependencies.translateFn('Delete company?')
        : this.dependencies.translateFn('Delete companies?');

    const dialog = GzDialogWebComponent.computeCustomDialog(
      windowBodyHtml,
      windowFooterHtml,
      windowTitle,
      null,
      async () => {
        const confirmBtn = dialog.shadowRoot?.querySelector<GzButtonWebComponent>('#confirm');

        if (confirmBtn) {
          confirmBtn.showSpinner = true;
          confirmBtn.disabled = true;
        }

        await this.removeSelectedCompanyRecordsHandler(companyIds);

        dialog.close();
      },
      null,
      false
    );
  }

  private async removeSelectedCompanyRecordsHandler(companyIds: Array<{ _id: string }>) {
    try {
      // number of companies which contain other companies
      const numberOfCompaniesInContainer = await this.dataRepository.getNumberOfCompaniesInContainer(companyIds);

      if (numberOfCompaniesInContainer > 0) {
        this.showCannotDeleteSelectedCompaniesWindow(companyIds);
      } else {
        await this.dataRepository.removeCompanyRecords(companyIds);
        this.showSuccessToast(this.dependencies.translateFn('Deleted successfully.'));
        await this.view.dom.$companiesGrid.reload();
      }
    } catch (error) {
      this.showDeleteCompanyErrorToast(error);
      await this.view.dom.$companiesGrid.reload();
    }
  }

  private showCannotDeleteSelectedCompaniesWindow(companyIds: Array<{ _id: string }>) {
    const windowBodyHtml = `
      <p>
        ${
          companyIds.length === 1
            ? this.dependencies.translateFn('The selected company is managing other companies.')
            : this.dependencies.translateFn('One or more of the selected companies is managing other companies.')
        }
      </p>`;

    const windowFooterHtml = `<gz-button id='confirm' type='outline' color='negative' class='mr-2'>
       ${this.dependencies.translateFn('Ok')}
      </gz-button>`;

    const windowTitle =
      companyIds.length === 1
        ? this.dependencies.translateFn('Cannot delete the selected company')
        : this.dependencies.translateFn('Cannot delete the selected companies');

    GzDialogWebComponent.computeCustomDialog(windowBodyHtml, windowFooterHtml, windowTitle, null, null);
  }

  private showDeleteCompanyErrorToast(error: Error) {
    let errorMessage;

    if (error['exceptionType'] === 'Exceptions\\DeleteCompanySecurityException') {
      errorMessage = this.dependencies.translateFn('You need permission to delete the selected companies.');
    } else {
      errorMessage = this.translator.getGenericErrorMessage();
    }

    GzToastNotificationWebComponent.buildToastWrapperElement({
      message: errorMessage,
      severity: ToastSeverity.ERROR,
    });
  }

  private activateCompanies(event: OnActivateCompany): void {
    const companies = event.detail.companies;
    const suspendedCompanies = companies.filter(company => company.isSuspended);
    const suspendedPartnerCompanies = suspendedCompanies.filter(company => company.companyType === CompanyType.Partner);

    const windowTitle =
      suspendedCompanies.length === 1
        ? this.dependencies.translateFn('Activate company?')
        : this.dependencies.translateFn('Activate companies?');

    const dialogContent = new ActivateCompanyConfirmationWebComponent();
    dialogContent.suspendedCompaniesCount = suspendedCompanies.length;
    dialogContent.suspendedPartnerCompaniesCount = suspendedPartnerCompanies.length;
    dialogContent.translateFn = this.dependencies.translateFn;

    const windowFooterHtml = `<gz-button id='confirm' class='mr-2'>
       ${this.dependencies.translateFn('Activate')}
      </gz-button>
      <gz-button id='cancel' type='outline' color='secondary'>
        ${this.dependencies.translateFn('Cancel')}
      </gz-button>`;

    const dialog = GzDialogWebComponent.computeCustomDialog(
      dialogContent,
      windowFooterHtml,
      windowTitle,
      null,
      async () => {
        const recursiveToggleValue = dialogContent.recursiveToggle?.getFormValue() ?? false;
        const confirmBtn = dialog.shadowRoot?.querySelector<GzButtonWebComponent>('#confirm');

        if (confirmBtn) {
          confirmBtn.showSpinner = true;
          confirmBtn.disabled = true;
        }

        await this.setSuspendStatus(companies, false, recursiveToggleValue);

        // reload grid
        await this.view.dom.$companiesGrid.reload();

        dialog.close();
      },
      null,
      false
    );
  }

  private suspendCompanies(event: OnSuspendCompany): void {
    const companies = event.detail.companies;
    const activeCompanies = companies.filter(company => !company.isSuspended);
    const activePartnerCompanies = activeCompanies.filter(company => company.companyType === CompanyType.Partner);

    const windowTitle =
      activeCompanies.length === 1
        ? this.dependencies.translateFn('Suspend company?')
        : this.dependencies.translateFn('Suspend companies?');

    const dialogContent = new SuspendCompanyConfirmationWebComponent();
    dialogContent.activeCompaniesCount = activeCompanies.length;
    dialogContent.activePartnerCompaniesCount = activePartnerCompanies.length;
    dialogContent.translateFn = this.dependencies.translateFn;

    const windowFooterHtml = `<gz-button id='confirm' class='mr-2'>
       ${this.dependencies.translateFn('Suspend')}
      </gz-button>
      <gz-button id='cancel' type='outline' color='secondary'>
        ${this.dependencies.translateFn('Cancel')}
      </gz-button>`;

    const dialog = GzDialogWebComponent.computeCustomDialog(
      dialogContent,
      windowFooterHtml,
      windowTitle,
      null,
      async () => {
        const recursiveToggleValue = dialogContent.recursiveToggle?.getFormValue() ?? false;
        const confirmBtn = dialog.shadowRoot?.querySelector<GzButtonWebComponent>('#confirm');

        if (confirmBtn) {
          confirmBtn.showSpinner = true;
          confirmBtn.disabled = true;
        }

        await this.setSuspendStatus(companies, true, recursiveToggleValue);

        // reload grid
        await this.view.dom.$companiesGrid.reload();

        dialog.close();
      },
      null,
      false
    );
  }

  private modifyMspInProductTrialsRights(event: OnModifyMspInProductTrialsRights): void {
    const companyId = event.detail.companies[0].companyId;
    const status = event.detail.status;
    const productTrialId = event.detail.productTrialId;

    const {
      windowBodyHtml,
      windowTitle,
    } = this.generateWindowContent(productTrialId, status, false);

    const windowFooterHtml = `<gz-button id='confirm' type='outline' color='negative' class='mr-2'>
       ${status ? 'Allow' : 'Revoke access'}
      </gz-button>
      <gz-button id='cancel' type='outline' color='secondary'>
        ${'Cancel'}
      </gz-button>`;

    const dialog = GzDialogWebComponent.computeCustomDialog(
      windowBodyHtml,
      windowFooterHtml,
      windowTitle,
      null,
      async () => {
        const confirmBtn = dialog.shadowRoot?.querySelector<GzButtonWebComponent>('#confirm');

        if (confirmBtn) {
          confirmBtn.showSpinner = true;
          confirmBtn.disabled = true;
        }

        try {
          await ConsoleRequest.createRequest<any>({
            dataProviderType: DataProviderType.CORE,
            endpoint: 'MspInProductTrials',
            method: 'modifyMspInProductTrialsRights',
            payload: {
              companyId: companyId,
              status: status,
              trialId: productTrialId,
            },
          });
          showToast(ToastSeverity.SUCCESS, status ? 'Access granted.' : 'Access revoked and trials stopped.');
          await this.view.dom.$companiesGrid.reload();
        } catch (error) {
          if (error.err.code === MODIFY_MSP_IPT_FLAGS_ERROR_DISTRIBUTOR_DOES_NOT_RESELL_PRODUCT) {
            showToast(ToastSeverity.ERROR, 'Request failed. The distributor does not have MDR Foundation resell rights.');
          } else {
            showToast(ToastSeverity.ERROR, status ? 'Failed to grant access.' : 'Failed to revoke access and stop trials.');
          }
        } finally {
          dialog.close();
        }
      },
      null,
      false,
    );
  }

  private modifyMspInProductTrialsVisibility(event: OnModifyMspInProductTrialsRights): void {
    const companyId = event.detail.companies[0].companyId;
    const status = event.detail.status;
    const productTrialId = event.detail.productTrialId;

    const {
      windowBodyHtml,
      windowTitle,
    } = this.generateWindowContent(productTrialId, status, true);

    const windowFooterHtml = `<gz-button id='confirm' type='outline' color='negative' class='mr-2'>
       ${status ? 'Allow' : 'Revoke visibility'}
      </gz-button>
      <gz-button id='cancel' type='outline' color='secondary'>
        ${'Cancel'}
      </gz-button>`;

    const dialog = GzDialogWebComponent.computeCustomDialog(
      windowBodyHtml,
      windowFooterHtml,
      windowTitle,
      null,
      async () => {
        const confirmBtn = dialog.shadowRoot?.querySelector<GzButtonWebComponent>('#confirm');

        if (confirmBtn) {
          confirmBtn.showSpinner = true;
          confirmBtn.disabled = true;
        }

        try {
          await ConsoleRequest.createRequest<any>({
            dataProviderType: DataProviderType.CORE,
            endpoint: 'MspInProductTrials',
            method: 'modifyMspInProductTrialsVisibility',
            payload: {
              companyId: companyId,
              status: status,
              trialId: productTrialId,
            },
          });
          showToast(ToastSeverity.SUCCESS, status ? 'Visibility granted' : 'Visibility revoked.');
          await this.view.dom.$companiesGrid.reload();
        } catch (error) {
          showToast(ToastSeverity.ERROR, status ? 'Failed to grant visibility.' : 'Failed to revoke visibility.');
        } finally {
          dialog.close();
        }
      },
      null,
      false,
    );
  }

  private generateWindowContent(productTrialId: MspInProductTrialId, mspIptStatus: boolean, isVisibility: boolean) {
    const trialData = isVisibility ? {
        [MspInProductTrialId.MdrTrial]: {
          firstMessage: mspIptStatus ? 'The selected company will be able to view the MDR Product Trial card in the Product Trials page.' : 'The selected company will no longer be able to view the MDR Product Trial card in the Product Trials page.',
          secondMessage: 'This option will apply recursively.',
          title: mspIptStatus ? 'Allow companies to view the MDR Product Trial card?' : 'Revoke the visibility of the MDR Product Trial card?'
        },
        [MspInProductTrialId.EdrTrial]: {
          firstMessage: mspIptStatus ? 'The selected company will be able to view the EDR Product Trial card in the Product Trials page.' : 'The selected company will no longer be able to view the EDR Product Trial card in the Product Trials page.',
          secondMessage: 'This option will apply recursively.',
          title: mspIptStatus ?'Allow companies to view the EDR Product Trial card?' : 'Revoke the visibility of the EDR Product Trial card?'
        }
      } : {
        [MspInProductTrialId.MdrTrial]: {
          firstMessage: mspIptStatus ? 'The selected company will be able to enroll client companies in MDR from the Product Trials page.' : 'The selected company will no longer be able to enroll client companies in MDR from the Product Trials page.',
          secondMessage: mspIptStatus ? 'This option will apply recursively.' : 'All ongoing trials will end and enrolled companies will no longer have access to the MDR service.',
          title: mspIptStatus ? 'Allow companies to enroll in MSP MDR Product Trials?' : 'Revoke access to MSP MDR Product Trials?'
        },
        [MspInProductTrialId.EdrTrial]: {
          firstMessage: mspIptStatus ? 'The selected company will be able to enroll client companies in EDR from the Product Trials page.' : 'The selected company will no longer be able to enroll client companies in EDR from the Product Trials page.',
          secondMessage: mspIptStatus ? 'This option will apply recursively.' : 'All ongoing trials will end and enrolled companies will no longer have access to the EDR service.',
          title: mspIptStatus ? 'Allow companies to enroll in MSP EDR Product Trials?' : 'Revoke access to MSP EDR Product Trials?'
        }
      };

    const {
      firstMessage,
      secondMessage,
      title
    } = trialData[productTrialId] || {};

    return {
      windowBodyHtml: `<p>${firstMessage}</p> <br> <p>${secondMessage}</p>`,
      windowTitle: title
    };
  }

  private async setSuspendStatus(companies: Company[], isSuspended: boolean, recursive: boolean) {
    try {
      const response = await this.dependencies.api.clients.Companies.SetSuspendStatus(
        companies.map(company => {
          return {_id: company.companyId};
        }),
        isSuspended,
        recursive
      ).toPromise();

      if (!isSuspended && Object.keys(response.unprocessedCompanies).length > 0) {
        this.showReactivateErrorDialogue(response.unprocessedCompanies, companies.length);
        return;
      }

      GzToastNotificationWebComponent.buildToastWrapperElement({
        message: isSuspended
          ? this.dependencies.translateFn('Suspended successfully.')
          : this.dependencies.translateFn('Activated successfully.'),
        severity: ToastSeverity.SUCCESS,
      });
    } catch (error) {
      GzToastNotificationWebComponent.buildToastWrapperElement({
        message: this.translator.getGenericErrorMessage(),
        severity: ToastSeverity.ERROR,
      });
    }
  }

  private showReactivateErrorDialogue(
    companies: { [companyId: string]: string },
    totalNumberOfTargetCompanies: number
  ) {
    const errorMessage = this.getErrorMessageForReactivate(companies, totalNumberOfTargetCompanies);

    const windowTitle = this.dependencies.translateFn('Activation failed');
    const windowBodyHtml = `<div>${errorMessage}</div>`;
    const windowFooterHtml = `<gz-button id='confirm' type='outline' color='negative' class='mr-2'>
       ${this.dependencies.translateFn('Ok')}
      </gz-button>`;

    GzDialogWebComponent.computeCustomDialog(windowBodyHtml, windowFooterHtml, windowTitle, null, null);
  }

  private getErrorMessageForReactivate(
    companies: { [companyId: string]: string },
    totalNumberOfTargetCompanies: number
  ) {
    const companiesArray = Object.values(companies);

    if (totalNumberOfTargetCompanies > 1) {
      let companiesList = '<ul><li>';

      const maxListedCompanies = 5;

      companiesList +=
        companiesArray.length > maxListedCompanies + 1
          ? sanitizeArray(companiesArray.slice(0, maxListedCompanies)).join('</li><li>') +
            '</li><li><em>' +
            this.dependencies.translateFn('+{noOfItems} more', {
              noOfItems: companiesArray.length - maxListedCompanies,
            }) +
            '</em>'
          : sanitizeArray(companiesArray).join('</li><li>');

      companiesList += '</li></ul>';
      return (
        this.dependencies.translateFn(
          'These companies need active partners from which to inherit valid license subscriptions in order to be activated:'
        ) +
        '<br/>' +
        companiesList
      );
    } else {
      return this.dependencies.translateFn(
        'This company needs an active partner from which to inherit valid license subscriptions in order to be activated'
      );
    }
  }

  private showSuccessToast(message: string) {
    GzToastNotificationWebComponent.buildToastWrapperElement({
      message: message,
      severity: ToastSeverity.SUCCESS,
    });
  }

  private updateDialogHeadline(editCompanyPanelWebComp: EditCompanyPanelWebComponent, event: OnCompanyNameUpdates) {
    if (editCompanyPanelWebComp.started) {
      this.editCompanyDialog.headline = this.dependencies.translateFn('Edit {companyName}', {
        companyName: event.detail.companyName,
      });
    }
  }
}
